import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';

const TermsOfService = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      <nav className="navbar px-6 py-4 bg-white/80 backdrop-blur-xl border-b sticky top-0 z-50">
        <div className="container mx-auto flex justify-between items-center">
          <div className="flex items-center space-x-3 cursor-pointer" onClick={() => navigate('/')}>
            <img src="/logo.png" alt="AutomateSEO.ai" className="h-11 w-auto" />
            <span className="text-2xl font-bold heading bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">AutomateSEO.ai</span>
          </div>
          <Button variant="ghost" onClick={() => navigate('/')}>
            <ArrowLeft className="mr-2 h-4 w-4" />Back to Home
          </Button>
        </div>
      </nav>

      <div className="container mx-auto px-6 py-12 max-w-4xl">
        <h1 className="text-4xl font-bold mb-4 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Terms of Service</h1>
        <p className="text-gray-600 mb-8">Last Updated: December 7, 2025</p>
        
        <div className="prose prose-lg max-w-none space-y-8">
          <section>
            <h2 className="text-2xl font-bold mb-4 text-gray-900">1. Agreement to Terms</h2>
            <p className="text-gray-700 leading-relaxed">
              By accessing or using AutomateSEO.ai (the "Service"), operated by Set India Business Private Limited ("Company", "we", "us", or "our"), you agree to be bound by these Terms of Service. If you disagree with any part of these terms, you may not access the Service.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-gray-900">2. Description of Service</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              AutomateSEO.ai provides AI-powered digital marketing automation services including:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>SEO automation and optimization</li>
              <li>Local SEO and Google My Business management</li>
              <li>Social media content creation and scheduling</li>
              <li>AI-generated marketing content (text, images, videos)</li>
              <li>Analytics, reporting, and performance tracking</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-gray-900">3. User Accounts and Registration</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              To use our Service, you must:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>Provide accurate, current, and complete information during registration</li>
              <li>Maintain the security of your account password</li>
              <li>Notify us immediately of any unauthorized use of your account</li>
              <li>Be at least 18 years old or the legal age in your jurisdiction</li>
              <li>Be responsible for all activities under your account</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-gray-900">4. Subscription Plans and Billing</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>4.1 Subscription Tiers:</strong> We offer Starter and Pro plans with different feature sets and pricing.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>4.2 Billing Cycle:</strong> Subscriptions are billed monthly or annually based on your selected plan. Charges occur on the date you subscribe and renew automatically.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>4.3 Payment Methods:</strong> We accept credit cards, debit cards, and other payment methods as displayed during checkout.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>4.4 Price Changes:</strong> We reserve the right to change subscription prices with 30 days notice. Changes apply to subsequent billing cycles only.
            </p>
          </section>

          <section className="bg-blue-50 p-6 rounded-lg border-l-4 border-blue-500 mb-8">
            <h2 className="text-2xl font-bold mb-4 text-gray-900">Important: 3-Day Free Trial & Refund Policy</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>Free Trial:</strong> All new users receive a 3-day free trial to evaluate the service. No credit card required during trial.
            </p>
            <p className="text-gray-700 leading-relaxed">
              <strong className="text-red-600">Refund Policy:</strong> Refunds are ONLY available within 3 days from the date of payment. After 3 days, no refunds will be processed under any circumstances.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-gray-900">5. Free Trial and Subscription Terms</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>5.1 Trial Period:</strong> New users receive a 3-day free trial period with full access to Starter Plan features.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>5.2 Trial Termination:</strong> You may cancel your trial at any time during the 3-day period without charges.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>5.3 Post-Trial:</strong> After the trial expires, you must subscribe to a paid plan to continue using the service.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-gray-900">6. Refund Policy</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>6.1 Refund Window:</strong> Refunds are available ONLY within 3 days from the date of your first payment.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>6.2 No Refunds After 3 Days:</strong> Once 3 days have passed since your payment date, no refunds will be issued regardless of circumstances.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>6.3 Refund Process:</strong> To request a refund within the 3-day window, contact support@automateseo.ai with your account details and payment date.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>6.4 Processing Time:</strong> Approved refunds are processed within 5-7 business days.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-gray-900">7. Cancellation</h2>
            <p className="text-gray-700 leading-relaxed">
              We offer a 14-day free trial for new users. No credit card is required during the trial period. You may cancel anytime during the trial without charge. After the trial ends, your chosen subscription plan will begin unless you cancel.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-gray-900">6. Cancellation and Refunds</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              You may cancel your subscription at any time. Cancellation takes effect at the end of your current billing period. For our complete refund policy, please see our Refund Policy page.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-gray-900">7. Acceptable Use Policy</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              You agree NOT to use our Service to:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>Violate any laws or regulations</li>
              <li>Infringe on intellectual property rights</li>
              <li>Distribute spam, malware, or malicious content</li>
              <li>Generate fake reviews or engage in black-hat SEO practices</li>
              <li>Scrape or abuse our API beyond documented limits</li>
              <li>Resell our service without authorization</li>
              <li>Use automated tools to create accounts or abuse the Service</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-gray-900">8. Intellectual Property Rights</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>8.1 Our IP:</strong> The Service, including all software, algorithms, designs, and content (excluding user-generated content), is owned by Set India Business Private Limited and protected by intellectual property laws.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>8.2 User Content:</strong> You retain ownership of content you create using our Service. You grant us a license to use, process, and display your content solely to provide the Service.
            </p>
            <p className="text-gray-700 leading-relaxed">
              <strong>8.3 AI-Generated Content:</strong> Content generated by our AI (text, images, videos) is owned by you for commercial use as part of your subscription.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-gray-900">9. Data and Privacy</h2>
            <p className="text-gray-700 leading-relaxed">
              We are committed to protecting your data. Our Privacy Policy details how we collect, use, and protect your information. By using the Service, you consent to our data practices as described in the Privacy Policy.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-gray-900">10. Service Level Agreement (SLA)</h2>
            <p className="text-gray-700 leading-relaxed">
              We guarantee 99.9% uptime for paid subscriptions. If we fail to meet this standard, you may be eligible for service credits. See our complete SLA Agreement for details.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-gray-900">11. Limitation of Liability</h2>
            <p className="text-gray-700 leading-relaxed">
              To the maximum extent permitted by law, AutomateSEO.ai and Set India Business Private Limited shall not be liable for indirect, incidental, special, consequential, or punitive damages, or any loss of profits or revenues. Our total liability shall not exceed the amount you paid us in the 12 months preceding the claim.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-gray-900">12. Disclaimer of Warranties</h2>
            <p className="text-gray-700 leading-relaxed">
              The Service is provided "as is" without warranties of any kind, express or implied. We do not guarantee specific results, rankings, or traffic increases, though we strive to deliver optimal outcomes for all customers.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-gray-900">13. Termination</h2>
            <p className="text-gray-700 leading-relaxed">
              We may terminate or suspend your account immediately, without prior notice, for violations of these Terms. Upon termination, your right to use the Service ceases immediately. You may export your data within 30 days of termination.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-gray-900">14. Changes to Terms</h2>
            <p className="text-gray-700 leading-relaxed">
              We reserve the right to modify these Terms at any time. We will notify users of significant changes via email or in-app notification. Continued use of the Service after changes constitutes acceptance of the modified Terms.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-gray-900">15. Governing Law</h2>
            <p className="text-gray-700 leading-relaxed">
              These Terms shall be governed by and construed in accordance with the laws of India. Any disputes shall be resolved in the courts of Delhi, India.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-gray-900">16. Contact Information</h2>
            <p className="text-gray-700 leading-relaxed">
              For questions about these Terms, contact us at:
            </p>
            <p className="text-gray-700 mt-4">
              <strong>Email:</strong> legal@automateseo.ai<br/>
              <strong>Phone:</strong> +91 9810535398<br/>
              <strong>Address:</strong> Set India Business Private Limited, India
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;